import api from './api';

const adminApi = {
  // ==================== STAY MANAGEMENT ====================
  
  // Get all stays (admin view)
  getStays: async () => {
    try {
      const response = await api.get('/api/admin/stays');
      return response.data;
    } catch (error) {
      console.error('Error fetching stays:', error);
      throw error;
    }
  },

  // Get single stay
  getStay: async (stayId) => {
    try {
      const response = await api.get(`/api/admin/stays/${stayId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stay:', error);
      throw error;
    }
  },

  // Create new stay
  createStay: async (stayData) => {
    try {
      const response = await api.post('/api/admin/stays', stayData);
      return response.data;
    } catch (error) {
      console.error('Error creating stay:', error);
      throw error;
    }
  },

  // Update stay
  updateStay: async (stayId, stayData) => {
    try {
      const response = await api.put(`/api/admin/stays/${stayId}`, stayData);
      return response.data;
    } catch (error) {
      console.error('Error updating stay:', error);
      throw error;
    }
  },

  // Delete stay
  deleteStay: async (stayId) => {
    try {
      const response = await api.delete(`/api/admin/stays/${stayId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting stay:', error);
      throw error;
    }
  },

  // ==================== ROOM MANAGEMENT ====================

  // Get all rooms
  getRooms: async (stayId = null) => {
    try {
      const params = stayId ? { stayId } : {};
      const response = await api.get('/api/admin/rooms', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching rooms:', error);
      throw error;
    }
  },

  // Create new room
  createRoom: async (roomData) => {
    try {
      const response = await api.post('/api/admin/rooms', roomData);
      return response.data;
    } catch (error) {
      console.error('Error creating room:', error);
      throw error;
    }
  },

  // Update room
  updateRoom: async (stayId, roomId, roomData) => {
    try {
      const response = await api.put(`/api/admin/rooms/${stayId}/${roomId}`, roomData);
      return response.data;
    } catch (error) {
      console.error('Error updating room:', error);
      throw error;
    }
  },

  // Delete room
  deleteRoom: async (stayId, roomId) => {
    try {
      const response = await api.delete(`/api/admin/rooms/${stayId}/${roomId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting room:', error);
      throw error;
    }
  },

  // ==================== INVENTORY MANAGEMENT ====================

  // Get inventory
  getInventory: async (params = {}) => {
    try {
      const response = await api.get('/api/admin/inventory', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory:', error);
      throw error;
    }
  },

  // Bulk update inventory
  bulkUpdateInventory: async (inventoryData) => {
    try {
      const response = await api.post('/api/admin/inventory/bulk-update', inventoryData);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating inventory:', error);
      throw error;
    }
  },

  // Update single inventory record
  updateInventory: async (stayId, roomId, date, inventoryData) => {
    try {
      const response = await api.put(`/api/admin/inventory/${stayId}/${roomId}/${date}`, inventoryData);
      return response.data;
    } catch (error) {
      console.error('Error updating inventory:', error);
      throw error;
    }
  },

  // ==================== BOOKINGS MANAGEMENT ====================

  // Get all bookings (admin only)
  getBookings: async (params = {}) => {
    try {
      const response = await api.get('/api/bookings', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw error;
    }
  },

  approveBooking: async (bookingId, note = '') => {
    try {
      const response = await api.post(`/api/bookings/${bookingId}/approve`, { note });
      return response.data;
    } catch (error) {
      console.error('Error approving booking:', error);
      throw error;
    }
  },

  declineBooking: async (bookingId, note = '') => {
    try {
      const response = await api.post(`/api/bookings/${bookingId}/decline`, { note });
      return response.data;
    } catch (error) {
      console.error('Error declining booking:', error);
      throw error;
    }
  },

  cancelApprovedBooking: async (bookingId, note = '') => {
    try {
      const response = await api.post(`/api/bookings/${bookingId}/cancel`, { note });
      return response.data;
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw error;
    }
  },

  // ==================== DASHBOARD STATS ====================

  // Get dashboard statistics
  getStats: async () => {
    try {
      const response = await api.get('/api/admin/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  },

  // ==================== IMAGE UPLOAD ====================

  // Upload image (placeholder - would integrate with cloud storage)
  uploadImage: async (file) => {
    try {
      // In a real implementation, this would upload to S3
      // For now, return a placeholder URL
      return {
        url: URL.createObjectURL(file),
        filename: file.name,
        size: file.size
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  // Upload multiple images
  uploadImages: async (files) => {
    try {
      const uploadPromises = Array.from(files).map(file => adminApi.uploadImage(file));
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  }
};

export default adminApi;
